<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { validatePasswordStrength, resetPassword } from '../services/authService'
import { useUiStore } from '../stores/uiStore'

const route = useRoute()
const router = useRouter()
const uiStore = useUiStore()

const token = (route.query.token as string) || ''
const newPassword = ref('')
const confirmPassword = ref('')

const newPasswordError = ref('')
const confirmPasswordError = ref('')
const successMsg = ref('')
const isLoading = ref(false)
const tokenInvalid = ref(false)

onMounted(() => {
  if (!token) {
    tokenInvalid.value = true
  }
})

const handleReset = async () => {
  newPasswordError.value = ''
  confirmPasswordError.value = ''
  successMsg.value = ''

  if (!newPassword.value) {
    newPasswordError.value = '此欄不可留白'
    return
  }

  const strength = validatePasswordStrength(newPassword.value)
  if (!strength.valid) {
    newPasswordError.value = strength.error || '密碼強度不足'
    return
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = '此欄不可留白'
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    confirmPasswordError.value = '與新密碼不符'
    return
  }

  isLoading.value = true

  const result = await resetPassword(token, newPassword.value)

  if (result.success) {
    successMsg.value = '密碼已重置，請以新密碼登入'
  } else {
    newPasswordError.value = result.error || '重置失敗'
  }

  isLoading.value = false
}

const handleReturnHome = () => {
  router.push('/')
}

const errorFontSize = Math.max(8, Math.floor(uiStore.fontSize / 2))
</script>

<template>
  <div class="reset-password-container">
    <div class="reset-password-card">
      <h1>重置密碼</h1>

      <div v-if="tokenInvalid" class="error-box">
        <p>無效的重置連結。請重新申請密碼重置。</p>
        <button class="action-btn" @click="handleReturnHome">返回首頁</button>
      </div>

      <div v-else-if="successMsg" class="success-box">
        <p>{{ successMsg }}</p>
        <button class="action-btn primary" @click="handleReturnHome">返回首頁</button>
      </div>

      <div v-else class="form-container">
        <div class="form-group">
          <label>新密碼</label>
          <input
            v-model="newPassword"
            type="password"
            placeholder="輸入新密碼"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="newPasswordError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ newPasswordError }}
          </div>
        </div>

        <div class="form-group">
          <label>確認密碼</label>
          <input
            v-model="confirmPassword"
            type="password"
            placeholder="再次輸入新密碼"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="confirmPasswordError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ confirmPasswordError }}
          </div>
        </div>

        <div class="button-group">
          <button
            class="action-btn primary"
            @click="handleReset"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isLoading ? '重置中...' : '確認' }}
          </button>
          <button
            class="action-btn secondary"
            @click="handleReturnHome"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.reset-password-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f3f4f6;
  padding: 20px;
}

.reset-password-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 400px;
  width: 100%;
}

.reset-password-card h1 {
  text-align: center;
  color: #1f2937;
  margin-bottom: 24px;
  font-size: 24px;
}

.error-box {
  padding: 16px;
  background: #fee2e2;
  border-radius: 8px;
  text-align: center;
  color: #dc2626;
}

.error-box p {
  margin-bottom: 16px;
}

.success-box {
  padding: 16px;
  background: #ecfdf5;
  border-radius: 8px;
  text-align: center;
  color: #10b981;
}

.success-box p {
  margin-bottom: 16px;
  font-weight: 500;
}

.form-container {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  margin-top: 4px;
  font-size: 12px;
}

.button-group {
  display: flex;
  gap: 8px;
  margin-top: 24px;
}

.action-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.action-btn.primary {
  background: #3b82f6;
  color: white;
}

.action-btn.primary:hover:not(:disabled) {
  background: #2563eb;
}

.action-btn.secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.action-btn.secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.action-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  color: white;
}

.action-btn.secondary:disabled {
  background: #d1d5db;
  color: #6b7280;
}
</style>
